/**
 * AssessmentDesign.jsx — Phase 2A-4
 * Readiness Dashboard — 7-dimension explicit status, no opaque score.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell } from "../components/assessment-design/AssessmentDesignShell";
import { ReadinessCard } from "../components/assessment-design/ReadinessCard";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` }; }
async function apiFetch(path) {
  const r = await fetch(`${API}${path}`, { headers: authH() });
  if (!r.ok) throw new Error(`${r.status} ${await r.text().catch(() => "")}`);
  return r.json();
}

export default function AssessmentDesign() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policies, schemes, blueprints, itemBanks, rubrics, outcomes, items] = await Promise.all([
        apiFetch("/assessment/policies"),
        apiFetch("/assessment/schemes"),
        apiFetch("/assessment/blueprints"),
        apiFetch("/assessment/item-banks"),
        apiFetch("/assessment/rubrics"),
        apiFetch("/assessment/outcomes"),
        apiFetch("/assessment/items"),
      ]);
      setData({ policies, schemes, blueprints, itemBanks, rubrics, outcomes, items });
    } catch (e) {
      toast.error("Failed to load readiness data: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived readiness dimensions ──────────────────────────────────────────
  const publishedPolicies = data?.policies.filter(p => p.status === "PUBLISHED") ?? [];
  const publishedSchemes  = data?.schemes.filter(s => s.status === "PUBLISHED") ?? [];
  const publishedBPs      = data?.blueprints.filter(b => b.status === "PUBLISHED") ?? [];
  const draftBPs          = data?.blueprints.filter(b => b.status === "DRAFT") ?? [];
  const publishedOutcomes = data?.outcomes.filter(o => o.status === "PUBLISHED") ?? [];
  const approvedItems     = data?.items.filter(i => ["APPROVED", "PUBLISHED"].includes(i.status)) ?? [];
  const publishedRubrics  = data?.rubrics.filter(r => r.status === "PUBLISHED") ?? [];

  // Publish blockers list
  const blockers = [];
  if (!loading && data) {
    if (!publishedPolicies.length) blockers.push({ key: "policy", message: "No published assessment policy" });
    if (!publishedSchemes.length)  blockers.push({ key: "scheme", message: "No published grading scheme" });
    if (!publishedBPs.length && !draftBPs.length) blockers.push({ key: "blueprint", message: "No blueprint created" });
    if (draftBPs.length > 0 && !publishedBPs.length) blockers.push({ key: "blueprint-draft", message: `${draftBPs.length} blueprint(s) still in DRAFT — not published` });
    if (!approvedItems.length) blockers.push({ key: "items", message: "No approved/published items in any item bank" });
    if (!publishedRubrics.length) blockers.push({ key: "rubrics", message: "No published rubric" });
    if (!publishedOutcomes.length) blockers.push({ key: "outcomes", message: "No published learning outcome" });
  }

  return (
    <AssessmentDesignShell title="Assessment Design Workspace" breadcrumb="Readiness">
      {/* ── Summary header ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">Readiness Overview</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Explicit status across all 7 assessment-design dimensions. No single score — each dimension is independently verifiable.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="readiness-refresh-btn"
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* ── 7-Dimension readiness grid ───────────────────────────────────── */}
      <div
        data-testid="readiness-grid"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8"
      >
        <ReadinessCard
          testid="readiness-policy"
          label="Policy Status"
          state={loading ? "loading" : publishedPolicies.length ? "ready" : "missing"}
          detail={loading ? "Loading…" : publishedPolicies.length
            ? `${publishedPolicies.length} published policy`
            : "No published policy. Create and publish an assessment policy."}
          action={
            <Link to="/assessment-design/policies" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              Manage Policies <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />

        <ReadinessCard
          testid="readiness-scheme"
          label="Grading Scheme"
          state={loading ? "loading" : publishedSchemes.length ? "ready" : "missing"}
          detail={loading ? "Loading…" : publishedSchemes.length
            ? `${publishedSchemes.length} published scheme`
            : "No published scheme. A grading scheme is required before blueprint publication."}
          action={
            <Link to="/assessment-design/schemes" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              Manage Schemes <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />

        <ReadinessCard
          testid="readiness-blueprint"
          label="Assessment Blueprint"
          state={loading ? "loading" : publishedBPs.length ? "ready" : draftBPs.length ? "partial" : "missing"}
          detail={loading ? "Loading…" : publishedBPs.length
            ? `${publishedBPs.length} published blueprint`
            : draftBPs.length
              ? `${draftBPs.length} blueprint(s) in DRAFT — awaiting publish`
              : "No blueprint. Create a blueprint and add components."}
          action={
            <Link to="/assessment-design/blueprints" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              Manage Blueprints <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />

        <ReadinessCard
          testid="readiness-items"
          label="Item Pool"
          state={loading ? "loading" : approvedItems.length ? "ready" : "missing"}
          detail={loading ? "Loading…" : `${approvedItems.length} approved/published item(s)`}
          action={
            <Link to="/assessment-design/item-banks" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              Manage Item Banks <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />

        <ReadinessCard
          testid="readiness-rubrics"
          label="Rubric Completeness"
          state={loading ? "loading" : publishedRubrics.length ? "ready" : "missing"}
          detail={loading ? "Loading…" : publishedRubrics.length
            ? `${publishedRubrics.length} published rubric(s)`
            : "No published rubric. Create and publish at least one rubric."}
          action={
            <Link to="/assessment-design/rubrics" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              Manage Rubrics <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />

        <ReadinessCard
          testid="readiness-outcomes"
          label="Outcome Coverage"
          state={loading ? "loading" : publishedOutcomes.length ? "ready" : "missing"}
          detail={loading ? "Loading…" : `${publishedOutcomes.length} published learning outcome(s)`}
          action={
            <Link to="/assessment-design/outcomes" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1">
              Manage Outcomes <ArrowRight className="w-3 h-3" />
            </Link>
          }
        />
      </div>

      {/* ── Publish blockers ─────────────────────────────────────────────── */}
      {!loading && blockers.length > 0 && (
        <div
          data-testid="readiness-blockers-section"
          className="rounded-xl border border-amber-700/50 bg-amber-950/20 p-6 mb-8"
        >
          <h3 className="text-sm font-semibold text-amber-300 mb-3 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-800/50 text-amber-300 text-xs font-bold">{blockers.length}</span>
            Publish-readiness blockers
          </h3>
          <ul className="space-y-1.5">
            {blockers.map(b => (
              <li key={b.key} data-testid={`blocker-${b.key}`}
                  className="text-sm text-amber-200/80 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {b.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Guided journey reminder ──────────────────────────────────────── */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-4">Primary Design Journey</h3>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
          {[
            ["Policy", "/assessment-design/policies"],
            ["Scheme", "/assessment-design/schemes"],
            ["Blueprint", "/assessment-design/blueprints"],
            ["Components", "/assessment-design/blueprints"],
            ["Items / Rubrics", "/assessment-design/item-banks"],
            ["Outcomes", "/assessment-design/outcomes"],
            ["Validate", "/assessment-design/blueprints"],
            ["Publish", "/assessment-design/blueprints"],
          ].map(([label, to], i, arr) => (
            <React.Fragment key={label}>
              <Link to={to}
                    data-testid={`journey-step-${label.toLowerCase().replace(/\W+/g, "-")}`}
                    className="px-2.5 py-1 rounded-md border border-slate-700 bg-slate-800/60 text-slate-300 hover:border-sky-700 hover:text-sky-300 transition-colors">
                {label}
              </Link>
              {i < arr.length - 1 && <span className="text-slate-600">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </AssessmentDesignShell>
  );
}
